import React, { useEffect, useState } from 'react';

import {
  JabraRadioButton,
  JabraRadioButtonStyles,
  JabraRadioGroup,
} from 'common/components/JabraRadioGroup';

import JabraRangeSlider from '../../../common/components/JabraRangeSlider';
import { NavigationButtons } from '../components/NavigationButtons';
import { StepContainer } from '../components/StepContainer';
import { useHearingTest, useHearingTestTracking } from '../HearingTestContext';
import { translations } from '../translations/en';
import { STEP_NAMES, TOTAL_STEPS } from '../types/HearingTestTypes';
import { HearingTestEventAction } from '../types/TrackingTypes';

const HEARING_LEVEL_LABELS: { [key: number]: string } = {
  1: translations.verypoor,
  2: translations.poor,
  3: translations.good,
  4: translations.verygood,
};

export const GeneralQuestionsStep: React.FC = () => {
  const { state, updateProfile, goToStep } = useHearingTest();
  const { trackHearingTestEvent } = useHearingTestTracking();
  const [localProfile, setLocalProfile] = useState(state.profile);

  useEffect(() => {
    trackHearingTestEvent(HearingTestEventAction.LoadPage, 'Generic questions');
  }, [trackHearingTestEvent]);

  const handleNext = () => {
    trackHearingTestEvent(HearingTestEventAction.SelectGender, localProfile.gender || '');
    trackHearingTestEvent(HearingTestEventAction.SelectAge, localProfile.birthYear || '');
    trackHearingTestEvent(
      HearingTestEventAction.SelectHearingLevel,
      localProfile.hearingLevel || ''
    );
    trackHearingTestEvent(HearingTestEventAction.Click, 'Start test');

    updateProfile(localProfile);
    goToStep(STEP_NAMES.LIFESTYLE_QUESTIONS_1);
  };

  const handleInputChange = (field: keyof typeof localProfile, value: string | number | null) => {
    setLocalProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 120;
  const maxYear = currentYear - 4;
  const isBirthYearValid =
    !!localProfile.birthYear &&
    localProfile.birthYear >= minYear &&
    localProfile.birthYear <= maxYear;
  const isValid = localProfile.gender && isBirthYearValid && localProfile.hearingLevel;
  const genderError = localProfile.gender ? null : translations.genderhelp;
  const birthYearError = isBirthYearValid ? null : translations.yearbirthhelp;
  const hearingLevelError = localProfile.hearingLevel ? null : translations.moveslider;

  return (
    <StepContainer currentStep={state.currentStep} totalSteps={TOTAL_STEPS}>
      <div className="space-y-8">
        <div>
          <label className="mb-4 block text-lg font-semibold text-gray-900">
            {translations.gender} <span aria-label={translations.required}>*</span>
          </label>
          <JabraRadioGroup
            title="Gender Selection"
            selected={localProfile.gender || ''}
            onValueChange={(value) => handleInputChange('gender', value)}
            className="flex flex-col gap-3"
          >
            <JabraRadioButton
              value="male"
              title={translations.male}
              selectedStyle={JabraRadioButtonStyles.primary}
            />
            <JabraRadioButton
              value="female"
              title={translations.female}
              selectedStyle={JabraRadioButtonStyles.primary}
            />
            <JabraRadioButton
              value="prefer-not-to-say"
              title={translations.prefernottosay}
              selectedStyle={JabraRadioButtonStyles.primary}
            />
          </JabraRadioGroup>
          {genderError && <p className="mt-2 text-sm text-error-600">{genderError}</p>}
        </div>

        <div>
          <label htmlFor="birth-year" className="mb-4 block text-lg font-semibold text-gray-900">
            {translations.yearbirth} <span aria-label={translations.required}>*</span>
          </label>
          <input
            id="birth-year"
            type="number"
            min={minYear}
            max={maxYear}
            value={localProfile.birthYear || ''}
            onChange={(e) => handleInputChange('birthYear', parseInt(e.target.value) || null)}
            placeholder={`Enter year (${minYear}-${maxYear})`}
            aria-invalid={!isBirthYearValid}
            {...(!isBirthYearValid && { 'aria-describedby': 'birth-year-error' })}
            className="focus:ring-accent/20 w-full max-w-xs rounded-lg border border-gray-300 p-4 text-lg focus:border-accent focus:outline-none focus:ring-2"
          />
          {birthYearError && (
            <p id="birth-year-error" className="mt-2 text-sm text-error-600">
              {birthYearError}
            </p>
          )}
        </div>

        <div>
          <label
            id="hearing-level-label"
            className="mb-4 block text-lg font-semibold text-gray-900"
          >
            {translations.describehearing} <span aria-label={translations.required}>*</span>
          </label>
          <JabraRangeSlider
            ariaLabelledBy="hearing-level-label"
            values={[localProfile.hearingLevel || 0]}
            onValueChange={([value]) => handleInputChange('hearingLevel', value)}
            min={1}
            max={4}
            step={1}
            showStepLabels
            labelFormatter={(value: number) => HEARING_LEVEL_LABELS[value]}
          />
          {hearingLevelError && <p className="mt-2 text-sm text-error-600">{hearingLevelError}</p>}
        </div>
      </div>

      <NavigationButtons onNext={handleNext} disableNext={!isValid} />
    </StepContainer>
  );
};

export default GeneralQuestionsStep;
